import { useRef, useEffect, ReactNode } from 'react';

export interface InfiniteScrollContainerProps {
  children: ReactNode;
  /** Called when the sentinel scrolls into view */
  onLoadMore: () => void;
  /** Whether more pages are available */
  hasMore: boolean;
  /** Whether a page is currently being fetched */
  loadingMore?: boolean;
  /** Element shown while loadingMore is true */
  loader?: ReactNode;
  /** When true, shows a manual Load more button (e.g. after a fetch error) */
  error?: boolean;
  /** CSS class for the wrapper div */
  className?: string;
  /** How many px before the button enters view to trigger onLoadMore. Defaults to 400. */
  rootMargin?: number;
}

/**
 * Walk up the DOM to find the nearest ancestor that *actually* clips and
 * scrolls its content (i.e. has overflow auto/scroll AND its scrollable
 * dimension exceeds its client dimension). Returns null when no such
 * ancestor exists, which makes IntersectionObserver fall back to the
 * viewport — correct for window-level scrolling.
 */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el || el === document.body || el === document.documentElement) return null;
  const { overflow, overflowY, overflowX } = window.getComputedStyle(el);
  if (/auto|scroll/.test(overflow + overflowY + overflowX)) {
    if (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth) {
      return el;
    }
  }
  return getScrollParent(el.parentElement);
}

/**
 * Wraps content and calls `onLoadMore` via IntersectionObserver when the
 * sentinel scrolls into view. Automatically detects the nearest scrollable
 * ancestor as the observer root — or falls back to the viewport when the
 * component is used inside an unconstrained (responsive) container.
 *
 * The sentinel element is kept permanently in the DOM (hidden) so the
 * observer never loses its target across loading-state transitions.
 * A ref-based guard prevents double-calls while a fetch is in flight.
 */
export const InfiniteScrollContainer = ({
  children,
  onLoadMore,
  hasMore,
  loadingMore = false,
  loader,
  error = false,
  className = '',
  rootMargin = 400,
}: InfiniteScrollContainerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLSpanElement>(null);

  // Keep refs so the observer callback can read current values without
  // needing to be recreated every time loadingMore or error changes.
  const loadingMoreRef = useRef(loadingMore);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);

  const errorRef = useRef(error);
  useEffect(() => { errorRef.current = error; }, [error]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollParent = getScrollParent(wrapperRef.current?.parentElement ?? null);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMoreRef.current && !errorRef.current) onLoadMore();
      },
      { root: scrollParent, rootMargin: `0px 0px ${rootMargin}px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, rootMargin]);

  return (
    <div ref={wrapperRef} className={className}>
      {children}
      {hasMore && (
        <>
          {loadingMore
            ? loader
            : error && (
              <div className='flex center p32'>
                <button className="btn btn-primary" onClick={onLoadMore}>
                  Load more
                </button>
              </div>
            )
          }
          {/* Sentinel is always in the DOM so the observer never loses its target
              across loading-state transitions (loadingMore toggling). */}
          <span
            ref={sentinelRef}
            aria-hidden="true"
            style={{ display: 'block', height: 0, visibility: 'hidden' }}
          />
        </>
      )}
    </div>
  );
};
